import React, { useState, useEffect, useCallback, forwardRef, useRef } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  TablePagination,
  Button,
  TextField,
  Grid,
  Divider,
  LinearProgress,
  Chip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Slide,
  Skeleton,
  Zoom,
  Switch,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import axios from "axios";
import {
  CheckCircle,
  FileDownload,
  VisibilityOff,
  Search,
  Download,
  Close as CloseIcon,
  SearchOff as SearchOffIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  Check as CheckIcon,
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
} from "@mui/icons-material";
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import { useUser } from '../../contexts/UserContext'; // Assuming you have this context
import { Chart as ChartJS, ArcElement, Tooltip as ChartTooltip, Legend, LineElement, PointElement, LinearScale, CategoryScale, BarElement } from 'chart.js';
import { Pie, Line } from 'react-chartjs-2';
import CountUp from 'react-countup';

// Register Chart.js components
ChartJS.register(ArcElement, ChartTooltip, Legend, LineElement, PointElement, LinearScale, CategoryScale, BarElement);

// API URL
const API_BASE_URL = "https://croireatpl.in/api";
const REFRESH_INTERVAL_SECONDS = 180; // 3 minutes

// #region Helper Components

// Custom Styled Switch for the auto-refresh toggle
const CustomSwitch = styled(Switch)(({ theme }) => ({
  width: 58,
  height: 32,
  padding: 0,
  display: 'flex',
  '&:active': {
    '& .MuiSwitch-thumb': { width: 24 },
    '& .MuiSwitch-switchBase.Mui-checked': { transform: 'translateX(18px)' },
  },
  '& .MuiSwitch-switchBase': {
    padding: 4,
    color: '#bdbdbd',
    '&.Mui-checked': {
      transform: 'translateX(26px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.palette.primary.main,
      },
      '& .MuiSwitch-thumb': { backgroundColor: '#fff' },
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
    width: 24,
    height: 24,
    borderRadius: '50%',
    transition: theme.transitions.create(['width'], { duration: 200 }),
    backgroundColor: '#fff',
  },
  '& .MuiSwitch-track': {
    borderRadius: 32 / 2,
    opacity: 1,
    backgroundColor: 'rgba(0,0,0,.25)',
    boxSizing: 'border-box',
  },
}));

// StatsCard Component for the dashboard summary
const StatsCard = ({ title, value, icon, isAnimated = false, iconBgColor, increasePercent = 0 }) => {
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        p: 2.5,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        height: '100%',
        width: '100%',
        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
        },
      }}
    >
      <Typography variant="subtitle1"  sx={{ fontWeight: '600', mb: 2 }}>{title}</Typography>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexGrow: 1 }}>
        <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
          {isAnimated ? <CountUp end={value} duration={2} /> : value}
        </Typography>
        <Avatar sx={{ bgcolor: iconBgColor, borderRadius: '12px', width: 48, height: 48 }}>
          {React.cloneElement(icon, { sx: { color: "white", fontSize: '1.75rem' } })}
        </Avatar>
      </Box>
      <Box sx={{ mt: 1, minHeight: '20px' }}>
        {increasePercent > 0 && (
          <Box display="flex" alignItems="center" sx={{ color: 'success.main' }}>
            <TrendingUpIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{`${increasePercent.toFixed(0)}% this refresh`}</Typography>
          </Box>
        )}
      </Box>
    </Card>
  );
};

// HorizontalBarChart Component to display app versions as progress bars
const HorizontalBarChart = ({ data }) => {
  if (!data || !data.datasets || !data.datasets[0].data) return null;

  const labels = data.labels || [];
  const values = data.datasets[0].data || [];
  const total = values.reduce((sum, value) => sum + value, 0);

  const sortedData = labels
    .map((label, index) => ({ label, value: values[index] }))
    .sort((a, b) => b.value - a.value);

  return (
    <Box sx={{ width: '100%', p: 2, pt: 1 }}>
      {sortedData.map(({ label, value }) => {
        const percentage = total > 0 ? (value / total) * 100 : 0;
        return (
          <Box key={label} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">{label}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{value}</Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={percentage}
              sx={{ height: 10, borderRadius: 5, backgroundColor: 'grey.200' }}
            />
          </Box>
        );
      })}
    </Box>
  );
};

// StyledProgress Component for OperatorProfile
const StyledProgress = ({ icon, label, currentValue, totalValue, color }) => {
    const percentage = totalValue > 0 ? (currentValue / totalValue) * 100 : 0;
    return (
        <Box sx={{ width: '100%', mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ bgcolor: `${color}20`, width: 40, height: 40 }}>
                        {React.cloneElement(icon, { sx: { color: color } })}
                    </Avatar>
                    <Typography variant="subtitle1" sx={{ fontWeight: '500' }}>{label}</Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{`${currentValue} / ${totalValue}`}</Typography>
                    <Typography variant="body2" color="text.secondary">{`${percentage.toFixed(0)}%`}</Typography>
                </Box>
            </Box>
            <LinearProgress
                variant="determinate" value={percentage}
                sx={{ height: 8, borderRadius: 5, backgroundColor: 'grey.300', '& .MuiLinearProgress-bar': { backgroundColor: color, borderRadius: 5 } }}
            />
        </Box>
    );
};

// OperatorProfile Component
const OperatorProfile = ({ operator, enrollmentStats }) => {
    if (!operator) return null;
    return (
        <Card sx={{ borderRadius: 2, boxShadow: 0, overflow: "hidden" }}>
            <CardContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} sx={{ textAlign: "center", mt: 2, mb: 2 }}>
                        <Avatar src={operator.picture} alt="Operator" sx={{ width: 80, height: 80, margin: "0 auto", border: '3px solid #e0e0e0' }} />
                        <Typography variant="h5" sx={{ mt: 2, fontWeight: "bold" }}>{operator.operatorName}</Typography>
                        <Typography variant="subtitle1" color="text.secondary" sx={{ mt: 0.5 }}>
                            {operator.centerDetails?.centerName} ({operator.centerDetails?.centerCode})
                        </Typography>
                    </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <StyledProgress icon={<FileDownload />} label="Photos Downloaded" currentValue={operator.downloadedPhoto || 0} totalValue={operator.totalPhoto || 0} color="#ff9800" />
                {enrollmentStats && (
                    <>
                        <StyledProgress icon={<CheckCircle />} label="Enrollment by Operator" currentValue={enrollmentStats.studentsEnrolledByOperator} totalValue={enrollmentStats.totalStudentsInShift} color="#5666c9" />
                        <StyledProgress icon={<PeopleAltIcon />} label="Remaining Students" currentValue={enrollmentStats.totalStudentsInShift - enrollmentStats.studentsEnrolledByOperator} totalValue={enrollmentStats.totalStudentsInShift} color="#00796b" />
                        <Chip
                            label={enrollmentStats.operatorActivityStatus}
                            color={enrollmentStats.operatorActivityStatus === "High Activity" ? "success" : enrollmentStats.operatorActivityStatus === "Moderate Activity" ? "warning" : "default"}
                            variant="filled" sx={{ mt: 1, padding: "0.5em" }} icon={<CheckCircle />}
                        />
                    </>
                )}
            </CardContent>
        </Card>
    );
};

// ColumnVisibilityDialog Component
const ColumnVisibilityDialog = ({ open, onClose, columns, columnVisibility, onToggleColumn }) => (
    <Dialog open={open} onClose={onClose}>
        <DialogTitle>Show/Hide Columns</DialogTitle>
        <DialogContent dividers>
            <Grid container spacing={2}>
                {columns.map((col) => (
                    <Grid item xs={6} key={col.field}>
                        <Chip label={col.headerName} onClick={() => onToggleColumn(col.field)} color={columnVisibility[col.field] ? "primary" : "default"} variant={columnVisibility[col.field] ? "filled" : "outlined"} sx={{ cursor: 'pointer', width: '100%' }} />
                    </Grid>
                ))}
            </Grid>
        </DialogContent>
        <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
);

// ExportButtons Component
const ExportButtons = ({ onExportExcel, onExportPDF }) => {
  const { role } = useUser();
  if (role !== 'admin') return null;
  return (
    <Box sx={{ display: "flex", gap: 2 }}>
      <Button onClick={onExportExcel} variant="contained" color="primary" startIcon={<DescriptionIcon />}>Excel</Button>
      <Button onClick={onExportPDF} variant="contained" color="secondary" startIcon={<PictureAsPdfIcon />}>PDF</Button>
    </Box>
  );
};

// Transition for Dialogs
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Helper function to get initial shift from localStorage
const getInitialShift = () => {
  const savedDetails = localStorage.getItem("examDetails");
  return savedDetails ? JSON.parse(savedDetails).shift || "1" : "1";
};

// #endregion Helper Components


const OperatorPage = () => {
  // #region State and Refs
  const [operatorData, setOperatorData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [filterText, setFilterText] = useState("");
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [operatorProfileOpen, setOperatorProfileOpen] = useState(false);
  const [enrollmentStats, setEnrollmentStats] = useState(null);
  const [appVersionData, setAppVersionData] = useState(null);
  const [columnDialog, setColumnDialog] = useState(false);
  const [selectedShift, setSelectedShift] = useState(getInitialShift);
  const [appVersionFilter, setAppVersionFilter] = useState("All");
  const [isAutoRefreshOn, setIsAutoRefreshOn] = useState(true);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL_SECONDS);
  const [totalOperators, setTotalOperators] = useState(0);
  const [activeOperatorsCount, setActiveOperatorsCount] = useState(0);
  const [documentOperatorsCount, setDocumentOperatorsCount] = useState(0);
  const [increasePercents, setIncreasePercents] = useState({ total: 0, active: 0, docs: 0 });
  const [centerStatsOpen, setCenterStatsOpen] = useState(false);
  const [activeCenterStats, setActiveCenterStats] = useState([]);
  const [inactiveCenters, setInactiveCenters] = useState([]);
  const [tabValue, setTabValue] = useState("active");
  const [centerSortOrder, setCenterSortOrder] = useState("asc");
  const [centerSortField, setCenterSortField] = useState("centerCode");
  const [centerStatsCityFilter, setCenterStatsCityFilter] = useState("");
  const [chartType, setChartType] = useState('pie'); // 'pie' or 'bar'

  const lastUpdatedTimeRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const { role } = useUser();
  const allCenters = JSON.parse(localStorage.getItem("allCenters")) || [];
  // #endregion State and Refs

  // #region Columns and Data Logic
  const defaultColumns = [
    { field: "sNo", headerName: "S.No", visible: true },
    { field: "picture", headerName: "Picture", visible: true },
    { field: "operatorNumber", headerName: "Operator Number", visible: true },
    { field: "operatorName", headerName: "Operator Name", visible: true },
    { field: "centerCode", headerName: "Center Code", visible: true },
    { field: "centerName", headerName: "Center Name", visible: true },
    { field: "appVersion", headerName: "App Version", visible: true },
    { field: "operatorDocument", headerName: "Document", visible: true },
    { field: "currentLocation", headerName: "Location", visible: false },
  ];

  const [columnVisibility, setColumnVisibility] = useState(
    defaultColumns.reduce((acc, col) => ({ ...acc, [col.field]: col.visible }), {})
  );

  const formatDateToLocalISO = (date) => {
    const pad = (num) => String(num).padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    const timezoneOffset = -date.getTimezoneOffset();
    const sign = timezoneOffset >= 0 ? '+' : '-';
    const offsetHours = pad(Math.floor(Math.abs(timezoneOffset) / 60));
    const offsetMinutes = pad(Math.abs(timezoneOffset) % 60);
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${date.getMilliseconds()}${sign}${offsetHours}:${offsetMinutes}`;
  };

  const getCachedData = (shift) => {
    const cached = sessionStorage.getItem(`operatorData_shift_${shift}`);
    return cached ? JSON.parse(cached) : [];
  };

  const setCachedData = (shift, data) => {
    sessionStorage.setItem(`operatorData_shift_${shift}`, JSON.stringify(data));
  };

  const getCachedLastUpdatedTime = (shift) => {
    const cached = sessionStorage.getItem(`lastUpdatedTime_shift_${shift}`);
    return cached ? JSON.parse(cached) : null;
  };

  const setCachedLastUpdatedTime = (shift, time) => {
    sessionStorage.setItem(`lastUpdatedTime_shift_${shift}`, JSON.stringify(time));
  };

  const handleFetchData = useCallback(async (examName, shift, isInitialFetch = false) => {
    if (isInitialFetch) setLoading(true);
    try {
      const requestBody = { examName, shift };
      // if (lastUpdatedTimeRef.current) {
      //   const { seconds, nanos } = lastUpdatedTimeRef.current;
      //   const date = new Date(seconds * 1000 + (nanos || 0) / 1000000);
      //   requestBody.lastUpdatedTime = formatDateToLocalISO(date);
      // }

      const response = await axios.post(`${API_BASE_URL}/api/operators`, requestBody);
      const newData = response.data.data;

      if (newData && newData.length > 0) {
        // commented out caching logic for simplicity
   const latestTimestampObject = newData.reduce((latest, current) => 
          (current.lastUpdated?.seconds || 0) > (latest.lastUpdated?.seconds || 0) ? current : latest
        ).lastUpdated;

        if (latestTimestampObject) {
          lastUpdatedTimeRef.current = latestTimestampObject;
          setCachedLastUpdatedTime(shift, latestTimestampObject);
        }

        setOperatorData(prevData => {
          const operatorMap = new Map(prevData.map(op => [op.operatorNumber, op]));
          newData.forEach(op => operatorMap.set(op.operatorNumber, op));
          const updatedData = Array.from(operatorMap.values());
          setCachedData(shift, updatedData);
          return updatedData;
        });
      } else if (isInitialFetch) {
        setOperatorData([]);
        setCachedData(shift, []);
      }
    } catch (error) {
      console.error("Error fetching operator data:", error);
    } finally {
      if (isInitialFetch) setTimeout(() => setLoading(false), 500);
    }
  }, []);
  //       setOperatorData(newData);
  //   setCachedData(shift, newData);
  //     } else if (isInitialFetch || !newData || newData.length === 0) { // Also modify this line slightly
  //   setOperatorData([]);
  //   setCachedData(shift, []);
  // }
  //   } catch (error) {
  //     console.error("Error fetching operator data:", error);
  //   } finally {
  //     if (isInitialFetch) setTimeout(() => setLoading(false), 500);
  //   }
  // }, []);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem("examDetails")) || { examName: "DEMO", shift: "1" };
    const currentShift = selectedShift || userData.shift;
    // commented out caching logic for simplicity
    const cachedData = getCachedData(currentShift);
    const cachedLastUpdatedTime = getCachedLastUpdatedTime(currentShift);

    if (cachedData.length > 0) {
      setOperatorData(cachedData);
      lastUpdatedTimeRef.current = cachedLastUpdatedTime;
      setLoading(false);
    } else {
      sessionStorage.removeItem(`prevCounts_shift_${currentShift}`);
      setOperatorData([]);
      lastUpdatedTimeRef.current = null;
      handleFetchData(userData.examName, currentShift, true);
    }
    //  added new logic
  //    sessionStorage.removeItem(`prevCounts_shift_${currentShift}`);
  // setOperatorData([]); 
  // lastUpdatedTimeRef.current = null; 
  // handleFetchData(userData.examName, currentShift, true);

  setCountdown(REFRESH_INTERVAL_SECONDS);
}, [selectedShift, handleFetchData]);

  useEffect(() => {
    const newTotal = operatorData.length;
    const newActive = operatorData.length;
    const newDocs = operatorData.filter(op => op.operatorDocument).length;

    setTotalOperators(newTotal);
    setActiveOperatorsCount(newActive);
    setDocumentOperatorsCount(newDocs);

    const appVersions = operatorData.reduce((acc, op) => {
      const version = op.appVersion || "Unknown";
      acc[version] = (acc[version] || 0) + 1;
      return acc;
    }, {});

    if (Object.keys(appVersions).length > 0) {
      setAppVersionData({
        labels: Object.keys(appVersions),
        datasets: [{
          data: Object.values(appVersions),
          backgroundColor: ['#5666c9', '#00796b', '#f44336', '#ffc107', '#9c27b0', '#3f51b5'],
        }],
      });
    } else {
      setAppVersionData(null);
    }
  }, [operatorData, selectedShift]);

  const registrationData = operatorData.reduce((acc, op) => {
    const centerCode = op.centerDetails?.centerCode || "Unknown";
    acc[centerCode] = (acc[centerCode] || 0) + 1;
    return acc;
  }, {});

  const lineChartData = {
    labels: Object.keys(registrationData).sort(),
    datasets: [{
      label: 'Registrations by Center',
      data: Object.keys(registrationData).sort().map(code => registrationData[code]),
      borderColor: '#1976d2',
      backgroundColor: 'rgba(86, 102, 201, 0.2)',
      fill: true,
      tension: 0.4,
    }],
  };

  useEffect(() => {
    if (isAutoRefreshOn) {
      countdownIntervalRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            handleManualRefresh();
            return REFRESH_INTERVAL_SECONDS;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(countdownIntervalRef.current);
  }, [isAutoRefreshOn]);

  const handleManualRefresh = () => {
    const userData = JSON.parse(localStorage.getItem("examDetails")) || { examName: "DEMO" };
    
    handleFetchData(userData.examName, selectedShift, false);
    setCountdown(REFRESH_INTERVAL_SECONDS);
  };

  const handleRowClick = useCallback(async (operator) => {
    setSelectedOperator(operator);
    setOperatorProfileOpen(true);
    try {
        const response = await axios.post(`${API_BASE_URL}/students/enrollment-stats`, {
            examName: (JSON.parse(localStorage.getItem("examDetails")) || {}).examName,
            shift: selectedShift,
            centerCode: operator.centerDetails?.centerCode,
            operatorNumber: operator.operatorNumber,
        });
        setEnrollmentStats(response.data);
    } catch (error) {
        console.error("Failed to fetch enrollment stats:", error);
        setEnrollmentStats(null);
    }
  }, [selectedShift]);

const handleExportCenterStatsExcel = () => {
  const filteredActiveCenters = sortedActiveCenters.filter(c => (c.city || "").toLowerCase().includes(centerStatsCityFilter.toLowerCase()));
  const filteredInactiveCenters = sortedInactiveCenters.filter(c => (c.city || "").toLowerCase().includes(centerStatsCityFilter.toLowerCase()));

  const activeData = filteredActiveCenters.map((stat, index) => ({
    "S.No": index + 1,
    "Center Name": stat.centerName,
    "Center Code": stat.centerCode,
    "City": stat.city,
    "Active Operators": stat.activeCount,
    "Device Info": stat.deviceIdsStr || "N/A",
    "Status": "Active",
  }));

  const inactiveData = filteredInactiveCenters.map((center, index) => ({
    "S.No": index + 1,
    "Center Name": center.centerName,
    "Center Code": center.centerCode,
    "City": center.city,
    "Active Operators": 0,
    "Device Info": "N/A",
    "Status": "Inactive",
  }));

  const exportData = [
    ...activeData,
    ...(activeData.length > 0 && inactiveData.length > 0 ? [{ "S.No": "", "Center Name": "", "Center Code": "", "City": "", "Active Operators": "", "Device Info": "", "Status": "" }] : []), // Separator row
    ...inactiveData,
  ];

  const ws = XLSX.utils.json_to_sheet(exportData, {
    header: ["S.No", "Center Name", "Center Code", "City", "Active Operators", "Device Info", "Status"],
  });
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Center Stats");
  XLSX.writeFile(wb, `center_stats_shift_${selectedShift}.xlsx`);
};

const handleExportCenterStatsPDF = () => {
  const filteredActiveCenters = sortedActiveCenters.filter(c => (c.city || "").toLowerCase().includes(centerStatsCityFilter.toLowerCase()));
  const filteredInactiveCenters = sortedInactiveCenters.filter(c => (c.city || "").toLowerCase().includes(centerStatsCityFilter.toLowerCase()));

  const doc = new jsPDF();
  doc.text(`Center Statistics - Shift ${selectedShift}`, 14, 16);

  const activeData = filteredActiveCenters.map((stat, index) => [
    index + 1,
    stat.centerName,
    stat.centerCode,
    stat.city,
    stat.activeCount,
    stat.deviceIdsStr || "N/A",
    "Active",
  ]);

  const inactiveData = filteredInactiveCenters.map((center, index) => [
    index + 1,
    center.centerName,
    center.centerCode,
    center.city,
    0,
    "N/A",
    "Inactive",
  ]);

  const tableData = [
    ...activeData,
    ...(activeData.length > 0 && inactiveData.length > 0 ? [["", "", "", "", "", "", ""]] : []), // Separator row
    ...inactiveData,
  ];

  doc.autoTable({
    head: [["S.No", "Center Name", "Center Code", "City", "Active Operators", "Device Info", "Status"]],
    body: tableData,
    startY: 20,
    styles: { fontSize: 8 },
    headStyles: { fillColor: [86, 102, 201] }, // Match primary color (#5666c9)
    didDrawCell: (data) => {
      if (data.row.raw[6] === "" && data.row.section === "body") {
        data.cell.styles.fillColor = [200, 200, 200]; // Gray background for separator
      }
    },
  });

  doc.save(`center_stats_shift_${selectedShift}.pdf`);
};

  const handleExportExcel = () => {
    const exportData = filteredOperatorData.map((operator, index) => ({
      "S.No": index + 1,
      "Operator Name": operator.operatorName ?? "N/A",
      "Operator Number": operator.operatorNumber ?? "N/A",
      "Center Code": operator.centerDetails?.centerCode ?? "N/A",
      "Center Name": operator.centerDetails?.centerName ?? "N/A",
      "App Version": operator.appVersion ?? "N/A",
      "Document Provided": operator.operatorDocument ? "Yes" : "No",
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Operators");
    XLSX.writeFile(wb, `operators_shift_${selectedShift}.xlsx`);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text(`Operator Data - Shift ${selectedShift}`, 14, 16);
    const tableData = filteredOperatorData.map((operator, index) => [
      index + 1,
      operator.operatorName ?? "N/A",
      operator.operatorNumber ?? "N/A",
      operator.centerDetails?.centerCode ?? "N/A",
      operator.appVersion ?? "N/A",
      operator.operatorDocument ? "Yes" : "No",
    ]);
    doc.autoTable({
      head: [["S.No", "Operator Name", "Operator Number", "Center Code", "App Version", "Document"]],
      body: tableData,
      startY: 20,
    });
    doc.save(`operators_shift_${selectedShift}.pdf`);
  };

  const handleCenterSort = (field) => {
    const isAsc = centerSortField === field && centerSortOrder === "asc";
    setCenterSortOrder(isAsc ? "desc" : "asc");
    setCenterSortField(field);
  };

  const calculateCenterStats = () => {
    const centerMap = new Map();
    operatorData.forEach((op) => {
      const { centerName, centerCode } = op.centerDetails || {};
      if (!centerCode || !centerName) return;
      const key = `${centerCode}_${centerName}`;
      if (!centerMap.has(key)) {
        const centerInfo = allCenters.find(c => c.centerCode === centerCode) || {};
        const city = centerInfo.examCity || centerInfo.city || centerInfo.exam_city || "N/A";
        centerMap.set(key, { centerName, centerCode, city, activeCount: 0, deviceIds: new Set() });
      }
      centerMap.get(key).activeCount += 1;
      const dId = op.deviceInfo;
      if (dId) centerMap.get(key).deviceIds.add(dId);
    });
    const activeStatsArray = Array.from(centerMap.values()).map(center => ({
        ...center,
        deviceIdsStr: Array.from(center.deviceIds).join(", ") || "N/A"
    })).sort((a, b) => b.activeCount - a.activeCount);
    setActiveCenterStats(activeStatsArray);
    const activeCenterCodes = activeStatsArray.map(center => center.centerCode);
    const inactive = allCenters.filter(center => !activeCenterCodes.includes(center.centerCode)).map(c => ({
      ...c,
      city: c.examCity || c.city || c.exam_city || "N/A"
    }));
    setInactiveCenters(inactive);
    setTabValue("active");
    setCenterStatsCityFilter(""); // Initialize/clear filter when opening
    setCenterStatsOpen(true);
  };
  
  const handleTabChange = (event, newValue) => setTabValue(newValue);

  const getSortedCenters = (arr, field, order) => {
    return [...arr].sort((a, b) => {
      if (a[field] < b[field]) return order === "asc" ? -1 : 1;
      if (a[field] > b[field]) return order === "asc" ? 1 : -1;
      return 0;
    });
  };
  
  const sortedActiveCenters = getSortedCenters(activeCenterStats, centerSortField, centerSortOrder);
  const sortedInactiveCenters = getSortedCenters(inactiveCenters, centerSortField, centerSortOrder);
  
  const filteredActiveCenterStats = sortedActiveCenters.filter(c => (c.city || "").toLowerCase().includes(centerStatsCityFilter.toLowerCase()));
  const filteredInactiveCenterStats = sortedInactiveCenters.filter(c => (c.city || "").toLowerCase().includes(centerStatsCityFilter.toLowerCase()));
  
  const filteredOperatorData = operatorData.filter((operator) => {
    const textFilter = filterText.toLowerCase();
    const matchesText = (
      (operator.operatorName ?? "").toLowerCase().includes(textFilter) ||
      // (operator.operatorNumber ?? "").toLowerCase().includes(textFilter) ||
      (String(operator.operatorNumber ?? "")).toLowerCase().includes(textFilter) ||
      // (operator.centerDetails?.centerCode ?? "").toLowerCase().includes(textFilter)
      (String(operator.centerDetails?.centerCode ?? "")).toLowerCase().includes(textFilter) ||

      (operator.centerDetails?.centerName ?? "").toLowerCase().includes(textFilter)
    );
    const matchesVersion = appVersionFilter === "All" || (operator.appVersion === appVersionFilter);
    return matchesText && matchesVersion;
  });

  const availableAppVersions = ["All", ...new Set(operatorData.map(op => op.appVersion).filter(v => v))];
  // #endregion Data Fetching and Logic

  const [isDownloadingProfiles, setIsDownloadingProfiles] = useState(false);

  const handleDownloadAllProfilesExcel = async () => {
    setIsDownloadingProfiles(true);
    const examName = (JSON.parse(localStorage.getItem("examDetails")) || {}).examName || "DEMO";
    try {
      const exportData = [];
      const batchSize = 10;
      let sNo = 1;

      for (let i = 0; i < filteredOperatorData.length; i += batchSize) {
        const batch = filteredOperatorData.slice(i, i + batchSize);
        const batchPromises = batch.map(async (operator) => {
          let stats = null;
          try {
            const response = await axios.post(`${API_BASE_URL}/students/enrollment-stats`, {
              examName: examName,
              shift: selectedShift,
              centerCode: operator.centerDetails?.centerCode,
              operatorNumber: operator.operatorNumber,
            });
            stats = response.data;
          } catch (err) {
            console.error("Failed to fetch stats for", operator.operatorNumber);
          }

          const totalPhoto = operator.totalPhoto || 0;
          const downloadedPhoto = operator.downloadedPhoto || 0;
          const photoPercentage = totalPhoto > 0 ? ((downloadedPhoto / totalPhoto) * 100).toFixed(0) + '%' : '0%';
          
          let enrollmentStr = "N/A";
          let remainingStr = "N/A";
          let activityStr = "N/A";
          
          if (stats) {
            const totalInShift = stats.totalStudentsInShift || 0;
            const enrolled = stats.studentsEnrolledByOperator || 0;
            const enrollmentPercent = totalInShift > 0 ? ((enrolled / totalInShift) * 100).toFixed(0) + '%' : '0%';
            
            enrollmentStr = `${enrolled} / ${totalInShift} (${enrollmentPercent})`;
            remainingStr = `${totalInShift - enrolled}`;
            activityStr = stats.operatorActivityStatus || "N/A";
          }

          return {
            "S.No": sNo++,
            "Operator Name": operator.operatorName ?? "N/A",
            "Operator Number": operator.operatorNumber ?? "N/A",
            "Device Info": operator.deviceInfo || "N/A",
            "Center Name": operator.centerDetails?.centerName ?? "N/A",
            "Center Code": operator.centerDetails?.centerCode ?? "N/A",
            "App Version": operator.appVersion ?? "N/A",
            "Photos Downloaded": `${downloadedPhoto} / ${totalPhoto} (${photoPercentage})`,
            "Enrollment by Operator": enrollmentStr,
            "Remaining Students": remainingStr,
            "Operator Activity Status": activityStr,
          };
        });

        const batchResults = await Promise.all(batchPromises);
        exportData.push(...batchResults);
      }

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Operator Profiles");
      XLSX.writeFile(wb, `all_operator_profiles_shift_${selectedShift}.xlsx`);
    } catch (error) {
      console.error("Error exporting all operator profiles:", error);
      alert("Failed to export operator profiles.");
    } finally {
      setIsDownloadingProfiles(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, p: { xs: 2, md: 4 } }}>
      <Box sx={{ display: "flex", flexDirection: { xs: 'column', md: 'row' }, justifyContent: "flex-end", alignItems: "center", mb: 4, gap: 2 }}>
        <FormControlLabel
          control={<CustomSwitch checked={isAutoRefreshOn} onChange={(e) => setIsAutoRefreshOn(e.target.checked)} checkedIcon={<CheckIcon fontSize="small" />} />}
          label="Auto-Refresh"
        />
        {isAutoRefreshOn && <Chip label={`Next in: ${countdown}`} variant="outlined" color="primary" />}
        <Tooltip title="Refresh Now"><IconButton onClick={handleManualRefresh} color="primary"><RefreshIcon /></IconButton></Tooltip>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4, alignItems: 'stretch' }}>
        {loading ? (
          Array.from(new Array(4)).map((_, index) => (
            <Box key={index} sx={{ flex: '1 1 280px', minWidth: '280px' }}><Skeleton variant="rounded" height={180} /></Box>
          ))
        ) : (
          <>
            <Box sx={{ flex: '1 1 280px', minWidth: '280px' }}><StatsCard title="Total Operators" value={totalOperators} isAnimated icon={<PeopleAltIcon />} iconBgColor="#ff9800" increasePercent={increasePercents.total} /></Box>
            <Box sx={{ flex: '1 1 280px', minWidth: '280px' }}><StatsCard title="Active Operators" value={activeOperatorsCount} icon={<CheckCircle />} iconBgColor="#5666c9" increasePercent={increasePercents.active} /></Box>
            <Box sx={{ flex: '1 1 280px', minWidth: '280px' }}><StatsCard title="Operators with Docs" value={documentOperatorsCount} icon={<DescriptionIcon />} iconBgColor="#00796b" increasePercent={increasePercents.docs} /></Box>
            <Box sx={{ flex: '1 1 280px', minWidth: '280px' }}>
              {appVersionData ? (
                <Card variant="outlined" sx={{ display: 'flex', flexDirection: 'column', height: '100%', borderRadius: 3, backgroundColor: '#fff' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1,ml:2, pb: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: '600' }}>App Versions</Typography>
                    <ToggleButtonGroup value={chartType} exclusive onChange={(e, newType) => { if (newType) setChartType(newType); }} size="small">
                      <ToggleButton value="pie" aria-label="pie chart"><PieChartIcon /></ToggleButton>
                      <ToggleButton value="bar" aria-label="bar chart"><BarChartIcon /></ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                  {chartType === 'pie' ? (
                    <Box sx={{ flexGrow: 1, height: 150, width: '100%' }}>
                      <Pie data={appVersionData} options={{ maintainAspectRatio: false, responsive: true, plugins: { legend: false  } }} />
                    </Box>
                  ) : (
                    <Box sx={{ flexGrow: 1, height: 150, width: '100%', p: 1,overflowX: 'auto', }}>
                    <HorizontalBarChart data={appVersionData} />
                    </Box>
                  )}
                </Card>
              ) : ( <Skeleton variant="rounded" height="100%" /> )}
            </Box>
          </>
        )}
      </Box>

      {loading ? (
        <Skeleton variant="rounded" height={300} />
      ) : (
        <Card variant="elevation" sx={{ p: 3, borderRadius: 3, backgroundColor: '#fff', boxShadow: '0 2px 1px rgba(17, 17, 18, 0.3)', }}>
          <Typography variant="subtitle1" sx={{ fontWeight: '800', mb: 2 }}>Activity</Typography>
          <Box sx={{ height: 250 }}>
            <Line data={lineChartData} options={{ responsive: true, maintainAspectRatio: false,plugins: { legend: false  }  }} />
          </Box>
        </Card>
      )}

      <Paper sx={{ boxShadow: 3, borderRadius: 2, backgroundColor: '#fff', mt: 4 }}>
        <Grid container spacing={2} alignItems="center" sx={{ p: 2 }}>
            <Grid item xs={12} md={8}>
                <TextField fullWidth variant="outlined" label="Search by Name, Number, or Center Code..." value={filterText} onChange={(e) => setFilterText(e.target.value)} InputProps={{ startAdornment: <Search color="action" sx={{ mr: 1 }} /> }} />
            </Grid>
            <Grid item xs={12} md={4}>
                <FormControl fullWidth variant="outlined">
                    <InputLabel>Filter by App Version</InputLabel>
                    <Select value={appVersionFilter} onChange={(e) => setAppVersionFilter(e.target.value)} label="Filter by App Version">
                        {availableAppVersions.map((version) => (<MenuItem key={version} value={version}>{version}</MenuItem>))}
                    </Select>
                </FormControl>
            </Grid>
        </Grid>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, gap: 2, borderTop: '1px solid #eee' }}>
            <Button onClick={() => setColumnDialog(true)} variant="outlined" startIcon={<VisibilityOff />}>Columns</Button>
            <ExportButtons onExportExcel={handleExportExcel} onExportPDF={handleExportPDF} />
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                <Table sx={{ minWidth: 650 }}>
                    <TableHead>
                        <TableRow>{defaultColumns.filter(col => columnVisibility[col.field]).map((col) => (<TableCell align="center"  key={col.field} sx={{ backgroundColor: "primary.main", color: "white", fontWeight: 'bold' }}>{col.headerName}</TableCell>))}</TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            Array.from(new Array(rowsPerPage)).map((_, index) => (
                                <TableRow key={index}>{defaultColumns.filter(c => columnVisibility[c.field]).map(c => (<TableCell key={c.field}><Skeleton /></TableCell>))}</TableRow>
                            ))
                        ) : filteredOperatorData.length > 0 ? (
                            filteredOperatorData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((operator, index) => (
                                <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }} key={operator.operatorNumber}>
                                    <TableRow onClick={() => handleRowClick(operator)} hover sx={{ cursor: "pointer" }}>
                                        {columnVisibility.sNo && <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>}
                                        {columnVisibility.picture && <TableCell align="center"><Avatar src={operator.picture} alt="Op" /></TableCell>}
                                        {columnVisibility.operatorNumber && <TableCell align="center">{operator.operatorNumber}</TableCell>}
                                        {columnVisibility.operatorName && <TableCell align="center">{operator.operatorName}</TableCell>}
                                        {columnVisibility.centerCode && <TableCell align="center">{operator.centerDetails?.centerCode}</TableCell>}
                                        {columnVisibility.centerName && <TableCell align="center">{operator.centerDetails?.centerName}</TableCell>}
                                        {columnVisibility.appVersion && <TableCell align="center">{operator.appVersion || 'N/A'}</TableCell>}
                                        {columnVisibility.operatorDocument && (
                                            <TableCell align="center">
                                                {operator.operatorDocument ? (
                                                    <Button variant="contained" size="small" onClick={(e) => { e.stopPropagation(); window.open(operator.operatorDocument, "_blank"); }}>Download</Button>
                                                ) : (<Chip label="Missing" size="small" variant="outlined" />)}
                                            </TableCell>
                                        )}
                                        {columnVisibility.currentLocation && <TableCell align="center">N/A</TableCell>}
                                    </TableRow>
                                </Zoom>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={defaultColumns.length} align="center">
                                    <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                        <SearchOffIcon sx={{ fontSize: '4rem' }} color="disabled" />
                                        <Typography variant="h6">No Operators Found</Typography>
                                        <Typography color="text.secondary">Try adjusting your search filters.</Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
        <TablePagination
            rowsPerPageOptions={[5, 10, 25]} component="div" count={filteredOperatorData.length}
            rowsPerPage={rowsPerPage} page={page} onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>
      
      <Box sx={{ display: "flex", justifyContent: "flex-end", marginTop: 2, gap: 2 }}>
        <Button 
          variant="contained" 
          color="success" 
          onClick={handleDownloadAllProfilesExcel} 
          disabled={isDownloadingProfiles || filteredOperatorData.length === 0}
          startIcon={!isDownloadingProfiles ? <FileDownload /> : null}
        >
          {isDownloadingProfiles ? <CircularProgress size={24} color="inherit" /> : 'Export All Profiles'}
        </Button>
        <Button variant="contained" color="info" onClick={calculateCenterStats}>
          View Center Stats
        </Button>
      </Box>

      <Dialog open={operatorProfileOpen} TransitionComponent={Transition} onClose={() => setOperatorProfileOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Operator Profile <IconButton onClick={() => setOperatorProfileOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}><OperatorProfile operator={selectedOperator} enrollmentStats={enrollmentStats} /></DialogContent>
      </Dialog>

      <ColumnVisibilityDialog
        open={columnDialog} onClose={() => setColumnDialog(false)} columns={defaultColumns}
        columnVisibility={columnVisibility} onToggleColumn={(field) => setColumnVisibility({ ...columnVisibility, [field]: !columnVisibility[field] })}
      />
      
      <Dialog open={centerStatsOpen} TransitionComponent={Transition} onClose={() => setCenterStatsOpen(false)} maxWidth="md" fullWidth>
  <DialogTitle sx={{ backgroundColor: '#5666c9', color: 'white' }}>Center Statistics</DialogTitle>
  <Tabs value={tabValue} onChange={handleTabChange} variant="fullWidth" indicatorColor="primary" textColor="primary">
    <Tab label={`Active Centers (${filteredActiveCenterStats.length})`} value="active" />
    <Tab label={`Inactive Centers (${filteredInactiveCenterStats.length})`} value="inactive" />
  </Tabs>
  <DialogContent dividers sx={{ p: 2 }}>
    <TextField fullWidth variant="outlined" label="Filter by City..." value={centerStatsCityFilter} onChange={(e) => setCenterStatsCityFilter(e.target.value)} sx={{ mb: 2 }} InputProps={{ startAdornment: <Search color="action" sx={{ mr: 1 }} /> }} />
    <TableContainer sx={{ maxHeight: '55vh' }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell align="center">S.No</TableCell>
            <TableCell align="center" sx={{ cursor: 'pointer' }} onClick={() => handleCenterSort('centerName')}>
              Center Name {centerSortField === 'centerName' ? (centerSortOrder === 'asc' ? '↑' : '↓') : ''}
            </TableCell>
            <TableCell align="center" sx={{ cursor: 'pointer' }} onClick={() => handleCenterSort('centerCode')}>
              Center Code {centerSortField === 'centerCode' ? (centerSortOrder === 'asc' ? '↑' : '↓') : ''}
            </TableCell>
            <TableCell align="center" sx={{ cursor: 'pointer' }} onClick={() => handleCenterSort('city')}>
              City {centerSortField === 'city' ? (centerSortOrder === 'asc' ? '↑' : '↓') : ''}
            </TableCell>
            {tabValue === 'active' && (
              <>
                <TableCell align="center">Active Operators</TableCell>
                <TableCell align="center">Device Info</TableCell>
              </>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {tabValue === 'active' ? (
            filteredActiveCenterStats.map((stat, index) => (
              <TableRow key={index}>
                <TableCell align="center">{index + 1}</TableCell>
                <TableCell align="center">{stat.centerName}</TableCell>
                <TableCell align="center">{stat.centerCode}</TableCell>
                <TableCell align="center">{stat.city}</TableCell>
                <TableCell align="center">{stat.activeCount}</TableCell>
                <TableCell align="center">{stat.deviceIdsStr}</TableCell>
              </TableRow>
            ))
          ) : (
            filteredInactiveCenterStats.map((center, index) => (
              <TableRow key={index}>
                <TableCell align="center">{index + 1}</TableCell>
                <TableCell align="center">{center.centerName}</TableCell>
                <TableCell align="center">{center.centerCode}</TableCell>
                <TableCell align="center">{center.city}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </DialogContent>
  <DialogActions>
    {role === 'admin' && (
      <Box sx={{ display: "flex", gap: 2 }}>
        <Button onClick={handleExportCenterStatsExcel} variant="contained" color="primary" startIcon={<DescriptionIcon />}>Excel</Button>
        <Button onClick={handleExportCenterStatsPDF} variant="contained" color="secondary" startIcon={<PictureAsPdfIcon />}>PDF</Button>
      </Box>
    )}
    <Button onClick={() => setCenterStatsOpen(false)}>Close</Button>
  </DialogActions>
</Dialog>
    </Box>
  );
};

export default OperatorPage;
